'use client'

import { memo, useCallback, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { CRAFT_MEDIA } from './craft-media'

const DEEP_DIVE_CARDS = [
  {
    id: 'local-agent',
    title: 'Local agent',
    tagline: 'Chat + tools on your machine',
    description:
      'Warm local models, shell, browser, and approved connectors: inference stays on-device unless you opt into a cloud plugin.',
    ctaLabel: 'See capabilities',
    icon: CRAFT_MEDIA.icons.deskAgent,
    placement: 'right-mid-cw',
    scrollId: 'features',
  },
  {
    id: 'inkflow',
    title: 'InkFlow',
    tagline: 'Speak. It writes everywhere.',
    description:
      'System-wide dictation with on-device Whisper. Paste at your cursor in Slack, Docs, Mail, or your IDE.',
    ctaLabel: 'Explore InkFlow',
    icon: CRAFT_MEDIA.icons.inkflow,
    placement: 'right-high-ccw',
    scrollId: 'inkflow',
  },
  {
    id: 'crews',
    title: 'Crews & automations',
    tagline: 'Agents that stay under approval',
    description:
      'NinCrew flows, scheduled jobs, and tool runs with your consent. Private by default, automatable when you want.',
    ctaLabel: 'Download NINX',
    icon: CRAFT_MEDIA.icons.crews,
    placement: 'corner-bottom-cw',
    scrollId: 'download',
  },
] as const

type CraftDeepDiveProps = {
  onScrollTo: (id: string) => void
}

const EASE = [0.22, 1, 0.36, 1] as const

/**
 * baby: Deep Dive as craft paper stamps: expand on hover, not modern SaaS panels.
 */
export const CraftDeepDive = memo(function CraftDeepDive({ onScrollTo }: CraftDeepDiveProps) {
  const reduced = Boolean(useReducedMotion())
  const [activeId, setActiveId] = useState<string>(DEEP_DIVE_CARDS[0].id)

  const onActivate = useCallback((id: string) => {
    setActiveId(id)
  }, [])

  return (
    <div className="craft-deep" data-testid="craft-deep-dive">
      <div className="craft-deep__header">
        <p className="craft-section__eyebrow">Deep dive</p>
        <h2 className="craft-section__title">Three ways NINX earns the desktop.</h2>
        <p className="craft-section__body">
          Hover a paper card: local agent, voice writing, and automations under your approval.
        </p>
      </div>

      <div className="craft-deep__row" role="list">
        {DEEP_DIVE_CARDS.map((card, index) => {
          const expanded = reduced ? true : activeId === card.id
          return (
            <article
              key={card.id}
              role="listitem"
              tabIndex={0}
              aria-expanded={expanded}
              className={`craft-deep__card craft-deep__card--r${(index % 3) + 1}${
                expanded ? ' is-expanded' : ''
              }`}
              onMouseEnter={() => onActivate(card.id)}
              onFocus={() => onActivate(card.id)}
              onClick={() => onActivate(card.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onActivate(card.id)
                }
              }}
            >
              <span className="craft-deep__tape" aria-hidden />
              <div className="craft-deep__asset" aria-hidden>
                <img
                  src={card.icon}
                  alt=""
                  className={`craft-deep__icon craft-deep__icon--${card.placement}${
                    expanded ? ' is-live' : ''
                  }`}
                  draggable={false}
                />
              </div>

              <div className="craft-deep__copy">
                <p className="craft-deep__index">{String(index + 1).padStart(2, '0')}</p>
                <h3 className="craft-deep__title">{card.title}</h3>
                <p className="craft-deep__tagline">{card.tagline}</p>

                <AnimatePresence initial={false}>
                  {expanded ? (
                    <motion.div
                      key="open"
                      className="craft-deep__detail"
                      initial={reduced ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.35, ease: EASE }}
                    >
                      <p className="craft-deep__body">{card.description}</p>
                      <button
                        type="button"
                        className="craft-btn craft-btn--ghost craft-deep__cta"
                        onClick={(event) => {
                          event.stopPropagation()
                          onScrollTo(card.scrollId)
                        }}
                      >
                        {card.ctaLabel}
                      </button>
                    </motion.div>
                  ) : (
                    <motion.p
                      key="hint"
                      className="craft-deep__hint"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      Hover to open
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
})

CraftDeepDive.displayName = 'CraftDeepDive'
